import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getNavigationItems } from '@/lib/navigation';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useDashboard } from '@/context/DashboardContext';
import { MoreHorizontal, Repeat } from 'lucide-react';

interface MobileBottomNavProps {
  isGlobalDarkMode: boolean;
  onSubMenuOpen: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  activeParentMenuId?: string | null;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ isGlobalDarkMode, onSubMenuOpen, activeParentMenuId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspace, userRole } = useWorkspace();
  const { currentDashboard, switchDashboard } = useDashboard();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);

  // Close more menu when location changes
  useEffect(() => {
    setShowMoreMenu(false);
  }, [location.pathname]);

  const navigationItems = React.useMemo(() => {
    const baseHiddenIds = currentWorkspace?.appearance?.menu?.hiddenIds || [];
    const roleHiddenIds = currentWorkspace?.appearance?.menu?.roleHiddenIds || {};
    
    // Determine the actual hidden IDs for the current user
    let hiddenIds = baseHiddenIds;
    
    // If user has a role and it's not owner, check for overrides
    if (userRole && userRole !== 'owner') {
      if (Array.isArray(roleHiddenIds[userRole])) {
        hiddenIds = roleHiddenIds[userRole];
      }
    }

    const dashboardItems = getNavigationItems(currentDashboard);

    return dashboardItems
      .map(item => {
        if (hiddenIds.includes(item.id)) return null;
        if (item.hasSubItems && item.subItems) {
          const visibleSubItems = item.subItems.filter(subItem => !hiddenIds.includes(subItem.id));
          return { ...item, subItems: visibleSubItems };
        }
        return item;
      })
      .filter((item): item is typeof dashboardItems[0] => item !== null);
  }, [currentWorkspace?.appearance?.menu?.hiddenIds, currentWorkspace?.appearance?.menu?.roleHiddenIds, userRole, currentDashboard]);

  // Split items: first 4 in main bar, rest in "More" menu
  const mainNavItems = navigationItems.slice(0, 4);
  const moreNavItems = navigationItems.slice(4);

  const isActivePath = (item: any) => {
    const path = item.path;
    const currentPath = location.pathname;

    // If we're viewing a submenu (not navigated yet), only highlight the parent
    if (activeParentMenuId && activeParentMenuId === item.id) return true;
    if (activeParentMenuId && activeParentMenuId !== item.id) return false;

    // Special cases first
    // File manager should only match exactly /files
    if (item.id === 'file-manager') {
      return currentPath === path || currentPath.startsWith(path + '/');
    }

    // Brand should NOT match file-manager
    if (item.id === 'brand' && currentPath.startsWith('/files')) {
      return false;
    }

    // Exact match
    if (currentPath === path) return true;

    // For items with subitems, check if any subitem matches
    if (item.hasSubItems && item.subItems) {
      return item.subItems.some((subItem: any) =>
        currentPath === subItem.path || currentPath.startsWith(subItem.path + '/')
      );
    }

    // Check if current path starts with item path (for subroutes)
    if (currentPath.startsWith(path + '/')) {
      return true;
    }

    return false;
  };

  const handleItemClick = (item: any) => {
    if (item.hasSubItems && item.subItems && item.subItems.length > 0) {
      const firstSubItem = item.subItems[0];
      if (firstSubItem?.path) {
        navigate(firstSubItem.path);
      }
    } else {
      onSubMenuOpen(null);
      navigate(item.path);
    }
    setShowMoreMenu(false);
  };

  const isMoreMenuActive = moreNavItems.some(item => isActivePath(item));

  return (
    <>
      <div
        className={`mobile-bottom-nav fixed bottom-0 left-0 right-0 border-t z-40 ${
          isGlobalDarkMode ? 'bg-sidebar-accent border-sidebar-border' : 'bg-white border-gray-200'
        }`}
      >
        <nav className="flex items-center justify-around px-2" style={{ height: '4rem' }}>
          {mainNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item);

            return (
              <React.Fragment key={item.id}>
                <button
                  onClick={() => handleItemClick(item)}
                  className="flex items-center justify-center flex-1 py-1"
                >
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-full transition-all active:scale-90 ${
                      isActive
                        ? isGlobalDarkMode
                          ? 'bg-nav-active text-white shadow-lg'
                          : 'bg-sidebar-light-active text-gray-900 shadow-sm'
                        : isGlobalDarkMode
                        ? 'text-sidebar-foreground/70 active:bg-sidebar'
                        : 'text-gray-600 active:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                </button>


              </React.Fragment>
            );
          })}

          {/* More Menu Button (only if there are overflow items) */}
          {moreNavItems.length > 0 && (
            <button
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className="flex items-center justify-center flex-1 py-1"
            >
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all active:scale-90 ${
                  isMoreMenuActive
                    ? isGlobalDarkMode
                      ? 'bg-nav-active text-white shadow-lg'
                      : 'bg-sidebar-light-active text-gray-900 shadow-sm'
                    : isGlobalDarkMode
                    ? 'text-sidebar-foreground/70 active:bg-sidebar'
                    : 'text-gray-600 active:bg-gray-100'
                }`}
              >
                <MoreHorizontal className="w-5 h-5" />
              </div>
            </button>
          )}

          {/* Dashboard Switcher Button */}
          <button
            onClick={() => switchDashboard(currentDashboard === 'agency' ? 'crm' : 'agency')}
            className="flex items-center justify-center flex-1 py-1"
          >
            <div
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-all active:scale-90 border ${
                isGlobalDarkMode
                  ? 'text-sidebar-foreground/60 active:bg-sidebar border-sidebar-border bg-sidebar-accent/50'
                  : 'text-gray-500 active:bg-gray-100 border-gray-200 bg-gray-50'
              }`}
            >
              <Repeat className="w-4 h-4" />
            </div>
          </button>
        </nav>
      </div>

      {/* More Menu Popup */}
      {showMoreMenu && (
        <div
          ref={moreMenuRef}
          className={`fixed bottom-20 right-4 w-48 rounded-lg shadow-xl border z-50 ${
            isGlobalDarkMode ? 'bg-sidebar-accent border-sidebar-border' : 'bg-white border-gray-200'
          }`}
        >
          <div className="p-2">
            {moreNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActivePath(item);

              return (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                    isActive
                      ? 'bg-nav-active text-white'
                      : isGlobalDarkMode
                      ? 'text-sidebar-foreground/80 hover:bg-sidebar'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};
